/**
 * Trip Planner — standard ChatTurn model + SSE reducer (GON-5362).
 *
 * Replaces the previous 5-role (`user | thinking | tool_call | tool_result | assistant`)
 * heuristic-interleaved chat model with a 2-role (`user | assistant`) model where each
 * assistant turn carries its tool calls inline as a structured array, keyed by the
 * stable `tool_call_id` emitted by the backend (see `agent.py` SSEEvent — GON-5360).
 *
 * Structurally identical to OpenAI / Anthropic reference implementations.
 *
 * Per-session budget caps (GON-5359):
 *   The backend emits `error` SSE events with a `reason` field for budget/flow caps:
 *     - "tokens"        — per-turn token budget exhausted (recoverable, retry helps)
 *     - "iterations"    — per-turn iteration cap hit (recoverable)
 *     - "turns"         — per-session turn cap hit (NOT recoverable, must start new chat)
 *     - "rate_limited"  — per-user/min Redis rate limit (recoverable, wait + retry)
 *     - "loop_detected" — duplicate consecutive tool call (recoverable, rephrase)
 *   Generic LLM/tool errors leave `reason` undefined. The reducer propagates `reason`
 *   to `ChatTurn.errorReason` so `MessageThread` can render a "Start a new chat" CTA
 *   only when `errorReason === "turns"`. The `event.content` already carries the
 *   exact user-facing copy (see `api_search/lib/planner/budgets.py`), so the FE
 *   renders that verbatim — no client-side string lookup.
 *
 * Why a separate module:
 *   - Keeps `usePlannerChat.ts` thin (the hook owns network + state, not message logic).
 *   - The reducer is pure, so it's trivial to unit-test SSE event sequences without
 *     mounting React.
 */

// --- Wire types ---

export type ChatEventType =
  | "session"
  | "thinking"
  | "text"
  | "tool_progress"
  | "tool_call"
  | "tool_result"
  | "error"
  | "done";

export interface ChatEvent {
  type: ChatEventType;
  sequence_index?: number;
  content?: string;
  tool?: string;
  tool_call_id?: string;
  args?: Record<string, unknown>;
  widget_type?: string;
  data?: Record<string, unknown>;
  /**
   * Optional machine-readable failure category for `error` / `done` events,
   * emitted by the backend for budget caps and loop detection (GON-5359).
   * One of: "tokens" | "iterations" | "turns" | "rate_limited" | "loop_detected".
   * Generic LLM/tool failures omit this field.
   */
  reason?: string;
}

// --- Domain types ---

export type ToolCallStatus = "calling" | "complete" | "error";

export interface ToolCallEntry {
  id: string;
  name: string;
  args?: Record<string, unknown>;
  widgetType?: string;
  widgetData?: Record<string, unknown>;
  activitySummary?: string;
  status: ToolCallStatus;
}

export type ChatTurnStatus = "thinking" | "calling" | "complete" | "error";

export type ChatActivity =
  | { type: "text"; content: string }
  | { type: "tool"; toolCallId: string };

export interface ChatTurn {
  id: string;
  role: "user" | "assistant";
  content?: string;
  /** A tool digest arrived; show writing activity until model text resumes. */
  awaitingModelText?: boolean;
  activityMessage?: string;
  activity?: ChatActivity[];
  toolCalls?: ToolCallEntry[];
  isStreaming?: boolean;
  status?: ChatTurnStatus;
  errorMessage?: string;
  /**
   * Machine-readable failure category propagated from the backend `error` SSE
   * event's `reason` field (GON-5359). Only set when status === "error".
   * "turns" is the only non-recoverable value and is the trigger for the
   * "Start a new chat" CTA in `MessageThread`.
   */
  errorReason?: string;
}

// --- Reload converter ---

interface PersistedMessage {
  id: string;
  role: string;
  content?: string | null;
  tool_name?: string | null;
  tool_call_id?: string | null;
  tool_args?: Record<string, unknown> | null;
  tool_result?: Record<string, unknown> | null;
  widget_type?: string | null;
}

const TOOL_ROLES = new Set(["tool", "tool_result"]);

/**
 * Convert the API-expanded `PlannerMessageResponse[]` into `ChatTurn[]`.
 *
 * The API still emits the legacy expanded shape (one entry per tool_call and one per
 * tool_result) so we group them here:
 *   - `user` rows → one turn each
 *   - consecutive non-user rows → merged into a single assistant turn
 *   - tool calls and tool results inside that group are paired by `tool_call_id`
 *
 * This mirrors how the live SSE reducer groups iterations into a single bubble,
 * so reload renders identically to live streaming.
 */
export function chatTurnsFromMessages(
  messages: ReadonlyArray<PersistedMessage>
): ChatTurn[] {
  const turns: ChatTurn[] = [];
  let currentAssistant: ChatTurn | null = null;

  for (const msg of messages) {
    if (msg.role === "user") {
      currentAssistant = null;
      turns.push({
        id: msg.id,
        role: "user",
        content: msg.content ?? "",
      });
      continue;
    }

    if (currentAssistant === null) {
      currentAssistant = {
        id: msg.id,
        role: "assistant",
        toolCalls: undefined,
        status: "complete",
      };
      turns.push(currentAssistant);
    }

    if (msg.role === "assistant") {
      // Append the assistant's content (multiple iterations append with a space).
      if (msg.content) {
        appendActivityText(
          currentAssistant,
          (currentAssistant.activity?.[currentAssistant.activity.length - 1]
            ?.type === "text"
            ? " "
            : "") + msg.content
        );
        currentAssistant.content = currentAssistant.content
          ? `${currentAssistant.content} ${msg.content}`
          : msg.content;
      }
      // If this expansion entry represents a tool call (legacy expanded shape),
      // attach it.
      if (msg.tool_name && msg.tool_call_id) {
        const calls = currentAssistant.toolCalls ?? [];
        if (!calls.some((tc) => tc.id === msg.tool_call_id)) {
          calls.push({
            id: msg.tool_call_id,
            name: plannerToolName(msg.tool_name),
            args: msg.tool_args ?? undefined,
            status: "calling",
          });
        }
        appendActivityTool(currentAssistant, msg.tool_call_id);
        currentAssistant.toolCalls = calls;
      }
      continue;
    }

    if (TOOL_ROLES.has(msg.role)) {
      if (!msg.tool_call_id) continue;
      const calls = currentAssistant.toolCalls ?? [];
      const existing = calls.find((tc) => tc.id === msg.tool_call_id);
      if (existing) {
        existing.widgetType = msg.widget_type ?? undefined;
        existing.widgetData = msg.tool_result ?? undefined;
        existing.status = "complete";
      } else {
        calls.push({
          id: msg.tool_call_id,
          name: plannerToolName(msg.tool_name ?? ""),
          widgetType: msg.widget_type ?? undefined,
          widgetData: msg.tool_result ?? undefined,
          status: "complete",
        });
      }
      appendActivityTool(currentAssistant, msg.tool_call_id);
      currentAssistant.toolCalls = calls;
    }
  }

  return turns;
}

// --- SSE reducer ---

/**
 * Apply a single SSE `ChatEvent` to the turns list. Pure: returns a new list.
 *
 * Invariants:
 *   - `session` events do not mutate the turn list (the hook handles them).
 *   - `thinking` ensures a streaming assistant turn exists.
 *   - `text` appends to the streaming assistant turn (creates one if missing).
 *   - `tool_progress` creates an early placeholder while the SDK is still
 *     building tool args. `tool_call` fills that placeholder with final args.
 *   - `tool_call` / `tool_result` mutate the streaming assistant turn's
 *     `toolCalls`, paired by `tool_call_id`. Orphan results (no matching
 *     call_id) are dropped.
 *   - `done` finalizes the streaming assistant turn (status=complete, isStreaming=false).
 *   - `error` finalizes the streaming assistant turn with status=error.
 */
export function reduceChatTurn(
  turns: ChatTurn[],
  event: ChatEvent
): ChatTurn[] {
  switch (event.type) {
    case "session":
      return turns;

    case "thinking": {
      const next = ensureStreamingAssistant(turns, "thinking");
      const last = next[next.length - 1];
      last.activityMessage = activityMessageForThinking(event.content);
      return next;
    }

    case "text": {
      const next = ensureStreamingAssistant(turns, "calling");
      const last = next[next.length - 1];
      if (event.content) {
        if (isTransientTextEvent(event)) {
          applyTransientActivitySummary(last, event);
          last.awaitingModelText = true;
        } else {
          last.content = (last.content ?? "") + event.content;
          appendActivityText(last, event.content);
          last.awaitingModelText = false;
        }
      }
      return next;
    }

    case "tool_progress": {
      if (!event.tool_call_id || !event.tool) return turns;
      const toolName = plannerToolName(event.tool);
      const next = ensureStreamingAssistant(turns, "calling");
      const last = next[next.length - 1];
      appendActivityTool(last, event.tool_call_id);
      const existing = last.toolCalls ?? [];
      if (existing.some((tc) => tc.id === event.tool_call_id)) return next;
      last.toolCalls = [
        ...existing,
        {
          id: event.tool_call_id,
          name: toolName,
          status: "calling",
        },
      ];
      return next;
    }

    case "tool_call": {
      if (!event.tool_call_id || !event.tool) return turns;
      const toolName = plannerToolName(event.tool);
      const next = ensureStreamingAssistant(turns, "calling");
      const last = next[next.length - 1];
      appendActivityTool(last, event.tool_call_id);
      const existing = last.toolCalls ?? [];
      if (existing.some((tc) => tc.id === event.tool_call_id)) {
        last.toolCalls = existing.map((tc) =>
          tc.id === event.tool_call_id
            ? {
                ...tc,
                name: toolName,
                args: event.args,
                status: "calling" as const,
              }
            : tc
        );
        return next;
      }
      last.toolCalls = [
        ...existing,
        {
          id: event.tool_call_id,
          name: toolName,
          args: event.args,
          status: "calling",
        },
      ];
      return next;
    }

    case "tool_result": {
      if (!event.tool_call_id) return turns;
      const idx = lastAssistantIndex(turns);
      if (idx === -1 || !turns[idx].toolCalls) return turns;
      const prev = turns[idx];
      if (idx !== turns.length - 1 || !prev.isStreaming) return turns;
      if (!prev.toolCalls!.some((tc) => tc.id === event.tool_call_id))
        return turns;
      const updatedCalls = prev.toolCalls!.map((tc) =>
        tc.id === event.tool_call_id
          ? {
              ...tc,
              widgetType: event.widget_type,
              widgetData: event.data,
              status: "complete" as const,
            }
          : tc
      );
      const updated: ChatTurn = { ...prev, toolCalls: updatedCalls };
      return [...turns.slice(0, idx), updated, ...turns.slice(idx + 1)];
    }

    case "done": {
      const idx = lastAssistantIndex(turns);
      if (idx === -1) return turns;
      const prev = turns[idx];
      // GON-5359: the backend emits `done` after every stream — including
      // budget-cap streams that send `error` immediately before `done`. If
      // we set status to "complete" here we'd clobber the terminal error
      // status, and MessageThread (which gates both the error message and
      // the "Start a new chat" CTA on status==='error') would render
      // nothing for any cap. Preserve a terminal error; otherwise mark
      // the turn complete.
      const toolCalls = prev.toolCalls?.map((tc) =>
        tc.status === "calling" ? { ...tc, status: "error" as const } : tc
      );
      const updated: ChatTurn = {
        ...prev,
        toolCalls,
        isStreaming: false,
        status: prev.status === "error" ? "error" : "complete",
        awaitingModelText: false,
      };
      return [...turns.slice(0, idx), updated, ...turns.slice(idx + 1)];
    }

    case "error": {
      const next = ensureStreamingAssistant(turns, "error");
      const last = next[next.length - 1];
      last.toolCalls = last.toolCalls?.map((call) =>
        call.status === "calling" ? { ...call, status: "error" as const } : call
      );
      last.status = "error";
      last.isStreaming = false;
      last.awaitingModelText = false;
      last.errorMessage = errorMessageForEvent(event.content);
      // GON-5359: surface the backend's machine-readable cap reason so
      // `MessageThread` can decide whether to show "Start a new chat" (only
      // when reason === "turns"). Generic errors omit `reason`, leaving
      // `errorReason` undefined — recoverable, no CTA.
      last.errorReason = event.reason;
      return next;
    }

    default:
      return turns;
  }
}

// --- helpers ---

function lastAssistantIndex(turns: ChatTurn[]): number {
  for (let i = turns.length - 1; i >= 0; i--) {
    if (turns[i].role === "assistant") return i;
  }
  return -1;
}

function isTransientTextEvent(event: ChatEvent): boolean {
  return event.data?.transient === true;
}

function applyTransientActivitySummary(turn: ChatTurn, event: ChatEvent): void {
  const summary = transientActivitySummary(event.content);
  if (!summary) return;

  if (!event.tool_call_id || !turn.toolCalls?.length) {
    turn.activityMessage = summary;
    return;
  }

  let matched = false;
  const toolCalls = turn.toolCalls.map((toolCall) => {
    if (toolCall.id !== event.tool_call_id) return toolCall;
    matched = true;
    if (toolCall.activitySummary === summary) return toolCall;
    return { ...toolCall, activitySummary: summary };
  });

  if (matched) {
    turn.toolCalls = toolCalls;
  } else {
    turn.activityMessage = summary;
  }
}

function transientActivitySummary(content: string | undefined): string {
  if (!content) return "";
  return content.replace(/\s*Writing the answer now\.$/, "").trim();
}

function errorMessageForEvent(content: string | undefined): string {
  if (!content) return "Something went wrong. Please try again.";
  if (content.toLowerCase().includes("connection lost")) {
    return "I lost track of this request. Please send it again.";
  }
  return content;
}

/**
 * Ensure the latest turn is a streaming assistant turn. Returns a **new** array
 * with a **cloned** last turn so callers can safely mutate the tail element
 * without corrupting previous React state.
 *
 * Status upgrades like `thinking` → `calling` are honored; we never re-upgrade
 * `calling` → `thinking`.
 */
function ensureStreamingAssistant(
  turns: ChatTurn[],
  desiredStatus: ChatTurnStatus
): ChatTurn[] {
  const last = turns[turns.length - 1];
  if (last && last.role === "assistant" && last.isStreaming) {
    const cloned: ChatTurn = { ...last };
    if (statusRank(desiredStatus) > statusRank(cloned.status)) {
      cloned.status = desiredStatus;
    }
    return [...turns.slice(0, -1), cloned];
  }
  return [
    ...turns,
    {
      id: createId(),
      role: "assistant",
      status: desiredStatus,
      isStreaming: desiredStatus !== "error",
    },
  ];
}

const THINKING_TOOL_ACTIVITY: Record<string, string> = {
  search_hotels: "Checking live hotel rates...",
  get_hotel_details: "Checking hotel details...",
  get_hotel_rates: "Checking hotel rates...",
  compare_rates: "Comparing cash and points...",
  get_booking_link: "Checking booking details...",
  book_hotel: "Checking booking details...",
  get_payment_methods: "Checking payment options...",
  get_rate_alerts: "Checking price monitoring...",
  create_rate_alert: "Updating price monitoring...",
  delete_rate_alert: "Updating price monitoring...",
  get_upcoming_trips: "Checking your trip context...",
  get_past_trips: "Checking your travel history...",
  get_loyalty_accounts: "Checking your loyalty accounts...",
  get_free_night_credits: "Checking your free night certificates...",
  get_favorite_hotels: "Checking your favorite hotels...",
  predict_price: "Checking price timing...",
  get_hotel_stats: "Checking price history...",
  get_multi_night_rates: "Checking flexible-date rates...",
  get_similar_hotels: "Checking similar hotels...",
  optimize_loyalty_portfolio: "Reviewing your loyalty points...",
  search_flights: "Checking flight options...",
  search_vehicles: "Checking rental car options...",
  search_places: "Checking local recommendations...",
  update_traveler_profile: "Saving your preferences...",
  get_traveler_snapshot: "Checking your traveler context...",
  analyze_trip_needs: "Reviewing your trip context...",
  get_chat_starters: "Finding personalized travel ideas...",
  explain_hotel_fit: "Checking hotel fit...",
};

function activityMessageForThinking(content: string | undefined): string {
  if (!content || content === "Processing your request...") {
    return "Starting your request...";
  }
  if (content === "Connecting to Gondola tools...") {
    return "Getting travel tools ready...";
  }
  if (content === "Reconnecting to your response...") {
    return "Reconnecting to your response...";
  }
  if (
    content === "Starting the response..." ||
    content === "Reading your request..." ||
    content === "Planning the next checks..." ||
    content === "Preparing a travel check..." ||
    content === "Reading travel results..." ||
    content === "Checking travel tool connection..." ||
    content === "Writing answer..."
  ) {
    return content;
  }
  const toolMatch = content.match(/^Using\s+([A-Za-z0-9_]+)\.\.\.$/);
  if (!toolMatch)
    return isSafeActivityMessage(content) ? content : "Thinking...";
  const toolName = plannerToolName(toolMatch[1]);
  return THINKING_TOOL_ACTIVITY[toolName] ?? "Checking travel tools...";
}

function isSafeActivityMessage(content: string): boolean {
  return (
    content.length > 0 &&
    content.length <= 90 &&
    !content.includes("_") &&
    !content.includes("mcp__") &&
    !/\b(api|tool|internal|debug|trace|token)\b/i.test(content)
  );
}

const GENERIC_WAITING_ACTIVITY = new Set([
  "Starting your request...",
  "Thinking...",
]);

const WAITING_ACTIVITY_STAGES = [
  { elapsedMs: 35_000, message: "Still working on the answer..." },
  { elapsedMs: 20_000, message: "Waiting on travel checks..." },
  { elapsedMs: 10_000, message: "Choosing the right travel checks..." },
  { elapsedMs: 4_000, message: "Getting travel tools ready..." },
] as const;

export function waitingActivityMessage(
  activityMessage: string | undefined,
  elapsedMs: number
): string {
  const fallback = activityMessageForThinking(activityMessage);
  if (!GENERIC_WAITING_ACTIVITY.has(fallback)) return fallback;

  for (const stage of WAITING_ACTIVITY_STAGES) {
    if (elapsedMs >= stage.elapsedMs) return stage.message;
  }

  return fallback;
}

function plannerToolName(toolName: string): string {
  const prefixes = ["mcp__gondola__", "mcp__gondola_chat__"];
  const prefix = prefixes.find((candidate) => toolName.startsWith(candidate));
  return prefix ? toolName.slice(prefix.length) : toolName;
}

function statusRank(s: ChatTurnStatus | undefined): number {
  switch (s) {
    case undefined:
      return -1;
    case "thinking":
      return 0;
    case "calling":
      return 1;
    case "complete":
      return 2;
    case "error":
      return 3;
  }
}

function createId(): string {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }
  return `turn-${Math.random().toString(36).slice(2)}`;
}

function appendActivityText(turn: ChatTurn, content: string): void {
  const activity = turn.activity ?? [];
  const last = activity[activity.length - 1];
  turn.activity =
    last?.type === "text"
      ? [...activity.slice(0, -1), { ...last, content: last.content + content }]
      : [...activity, { type: "text", content }];
}

function appendActivityTool(turn: ChatTurn, toolCallId: string): void {
  const activity = turn.activity ?? [];
  if (
    activity.some(
      (item) => item.type === "tool" && item.toolCallId === toolCallId
    )
  )
    return;
  turn.activity = [...activity, { type: "tool", toolCallId }];
}
